import { create } from 'zustand';
import { api } from '@/lib/api';
import type { MeResponse } from '@solidafy/api-client';
import { authClient } from '@/lib/auth-client';
import { config } from '@/lib/config';

export type LoginMethod = 'password-only' | 'sso-optional' | 'sso-required';

function loadStoredUser(): MeResponse | null {
  try {
    const stored = localStorage.getItem('user');
    return stored ? (JSON.parse(stored) as MeResponse) : null;
  } catch {
    return null;
  }
}

type AuthState = {
  user: MeResponse | null;
  activeOrgId: string | null;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  isAuthenticated: () => boolean;
  initAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean | '2fa'>;
  register: (data: { email: string; password: string; name?: string; organization_name?: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  markTourSeen: () => Promise<void>;
  clearAuth: () => void;
  setActiveOrg: (orgId: string) => void;
  checkEmail: (email: string) => Promise<LoginMethod>;
  loginWithSso: (email: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGitHub: () => Promise<void>;
  setError: (error: string | null) => void;
};

let initAuthPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: loadStoredUser(),
  activeOrgId: localStorage.getItem('active_org_id'),
  isLoading: false,
  error: null,
  isInitialized: false,

  isAuthenticated: () => !!get().user,

  initAuth: () => {
    if (initAuthPromise) {
      return initAuthPromise;
    }
    initAuthPromise = (async () => {
      try {
        // Check if we have a valid session via Better Auth
        const session = await authClient.getSession();
        if (session.data) {
          await get().getCurrentUser();
        } else {
          get().clearAuth();
        }
      } catch {
        get().clearAuth();
      }
      set({ isInitialized: true });
    })();
    return initAuthPromise;
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        set({ error: result.error.message || 'Login failed. Please check your credentials.', isLoading: false });
        return false;
      }
      // Better Auth returns twoFactorRedirect when 2FA is required
      if (result.data && 'twoFactorRedirect' in result.data && result.data.twoFactorRedirect) {
        set({ isLoading: false });
        return '2fa';
      }
      await get().getCurrentUser();
      set({ isLoading: false });
      return true;
    } catch {
      set({ error: 'Login failed. Please check your credentials.', isLoading: false });
      return false;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const result = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: data.name || data.email.split('@')[0],
        organization_name: data.organization_name,
      });
      if (result.error) {
        set({ error: result.error.message || 'Registration failed. Please try again.', isLoading: false });
        return false;
      }
      await get().getCurrentUser();
      set({ isLoading: false });
      return true;
    } catch {
      set({ error: 'Registration failed. Please try again.', isLoading: false });
      return false;
    }
  },

  logout: async () => {
    try {
      await authClient.signOut();
    } catch (err) {
      console.error('Logout error:', err);
    }
    get().clearAuth();
  },

  getCurrentUser: async () => {
    const { data, error } = await api.api.v1.auth.me.get();
    if (error || !data) {
      throw new Error('Failed to fetch user profile');
    }
    // Preserve the active org if it's still in the user's orgs, else default to the first
    const { activeOrgId } = get();
    const validOrg = data.organizations.find((o) => o.id === activeOrgId);
    const newActiveOrgId = validOrg ? activeOrgId : (data.organizations[0]?.id ?? null);
    if (newActiveOrgId !== activeOrgId) {
      if (newActiveOrgId) {
        localStorage.setItem('active_org_id', newActiveOrgId);
      } else {
        localStorage.removeItem('active_org_id');
      }
    }
    localStorage.setItem('user', JSON.stringify(data));
    set({ user: data, activeOrgId: newActiveOrgId });
  },

  markTourSeen: async () => {
    const { user } = get();
    if (!user || user.has_seen_tour) return;
    // Optimistically update so the tour doesn't re-trigger before the server responds
    const updated = { ...user, has_seen_tour: true };
    set({ user: updated });
    localStorage.setItem('user', JSON.stringify(updated));
    const { error } = await api.api.v1.auth.me['tour-seen'].patch();
    if (error) {
      // Roll back optimistic update — server didn't persist it, so the tour
      // may re-appear on other devices. Local state stays true to avoid
      // re-triggering in this session.
      console.warn('[auth] Failed to mark tour as seen on server:', error);
    }
  },

  clearAuth: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('product_mode');
    localStorage.removeItem('active_org_id');
    set({ user: null, activeOrgId: null, error: null, isLoading: false });
  },

  setActiveOrg: (orgId: string) => {
    localStorage.setItem('active_org_id', orgId);
    set({ activeOrgId: orgId });
  },

  checkEmail: async (email: string) => {
    try {
      const { data, error } = await api.api.v1.sso['check-email'].post({ email });
      if (error || !data) {
        return 'password-only';
      }
      return data.loginMethod;
    } catch {
      return 'password-only';
    }
  },

  loginWithSso: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      // Better Auth SSO sign-in endpoint — looks up provider by email domain
      const res = await fetch(`${config.apiUrl}/api/auth/sign-in/sso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          callbackURL: `${window.location.origin}/`,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        set({ error: body?.message ?? 'SSO login failed', isLoading: false });
        return;
      }
      const data = (await res.json()) as { url?: string };
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      set({ error: 'Failed to initiate SSO login', isLoading: false });
    }
  },

  loginWithGoogle: async () => {
    set({ isLoading: true, error: null });
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${window.location.origin}/`,
      });
    } catch {
      set({ error: 'Failed to initiate Google login', isLoading: false });
    }
  },

  loginWithGitHub: async () => {
    set({ isLoading: true, error: null });
    try {
      await authClient.signIn.social({
        provider: 'github',
        callbackURL: `${window.location.origin}/`,
      });
    } catch {
      set({ error: 'Failed to initiate GitHub login', isLoading: false });
    }
  },

  setError: (error: string | null) => set({ error }),
}));
